import { useCallback, useEffect, useRef, useState } from "react";
import { FileCheck2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/dateFormat";

// PART C (C4) — the signed delivery copy, attached to the order it belongs to.
// PART C FIX-UP (Fix 2a-c): a POD may be attached from DISPATCH onwards (that is the order a POD
// is most needed for), up to three files, and a phone photo is shrunk on the server.
export const PODBlock = ({ orderId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: resp } = await api.get(`/pod/orders/${orderId}`);
      setData(resp);
    } catch { setData(null); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: resp } = await api.post(`/pod/orders/${orderId}/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      const before = Math.round((resp.size_before_bytes || 0) / 1024);
      const after = Math.round((resp.size_after_bytes || 0) / 1024);
      toast.success(before > after ? `POD attached — ${before} KB shrunk to ${after} KB`
                                   : "POD attached");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "The POD could not be attached");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (podId) => {
    if (!window.confirm("Remove this POD from the order?")) return;
    try {
      await api.delete(`/pod/orders/${orderId}/file/${podId}`);
      toast.success("POD removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not remove it"); }
  };

  // nothing to show until the order has at least been dispatched
  if (!data || !data.dispatched) return null;

  return (
    <div className="border border-zinc-200 rounded-sm p-3 mb-3 bg-white" data-testid="pod-block">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
          <FileCheck2 className="w-3.5 h-3.5" /> Proof of delivery
          {data.pods.length === 0 && <span className="text-red-600 font-normal">— not on file yet</span>}
        </p>
        {data.can_upload && (
          <div>
            <input ref={inputRef} type="file" className="hidden" onChange={pick}
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" data-testid="pod-file-input" />
            <button className="h-7 px-2.5 text-[11px] border border-zinc-300 rounded-sm flex items-center gap-1 hover:bg-zinc-50"
              disabled={busy} onClick={() => inputRef.current?.click()} data-testid="pod-upload-btn">
              <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Upload POD"}
            </button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-zinc-400 mt-1">
        {data.pods.length} of {data.max_files || 3} file(s) · a photo is reduced to 1600 px before it is stored
        {!data.delivered && " · this order is not marked delivered yet"}
      </p>
      {data.pods.length > 0 && (
        <div className="mt-2 space-y-1">
          {data.pods.map(p => (
            <div key={p.pod_id} className="flex items-center gap-2 text-[11px] text-zinc-600"
              data-testid={`pod-file-${p.pod_id}`}>
              <span className="font-medium text-zinc-700" data-testid={`pod-name-${p.pod_id}`}>{String(p.filename || "POD file")}</span>
              {/* a RELATIVE href on purpose: the preview hides any anchor whose absolute URL
                  contains "emergent", which silently swallowed this link */}
              <a className="text-blue-700 underline" href={`/api/pod/orders/${orderId}/file/${p.pod_id}`}
                target="_blank" rel="noreferrer" data-testid={`pod-open-${p.pod_id}`}>Open</a>
              <span className="text-zinc-400">
                {fmtDate((p.uploaded_at || "").slice(0, 10))} · {p.uploaded_by_name}
                {p.size_bytes ? ` · ${Math.round(p.size_bytes / 1024)} KB` : ""}
              </span>
              {data.can_upload !== undefined && (
                <button className="text-red-600 hover:text-red-800" onClick={() => remove(p.pod_id)}
                  data-testid={`pod-remove-${p.pod_id}`}><Trash2 className="w-3 h-3" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
